import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Label,
} from "recharts";
import { clsx } from "clsx";
import {
  Building2,
  Calendar,
  DollarSign,
  CreditCard,
  ArrowDownCircle,
  ArrowUpCircle,
  Wallet,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../../../hooks/useAuth";
import { financialsApi } from "../../../lib/financialsApi";
import type { FinancialCategoryTotal } from "../../../lib/financialsApi";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { TransactionsDrawer } from "./TransactionsDrawer";
import { formatCurrency } from "../../../utils/format";

// ── Skeleton ──────────────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-[#e2e8f0] ${className ?? ""}`} />;
}

// ── Date filter ───────────────────────────────────────────────────────────────

type FilterType = "month" | "day" | "year" | "custom";

/** Format a local Date as YYYY-MM-DD without UTC conversion. */
function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getDefaultRange(type: FilterType, ref: string): { from: string; to: string } {
  const d = new Date(ref + "T00:00:00");
  if (type === "day") return { from: ref, to: ref };
  if (type === "month") {
    const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from, to: localDate(last) };
  }
  if (type === "year") {
    return { from: `${d.getFullYear()}-01-01`, to: `${d.getFullYear()}-12-31` };
  }
  return { from: ref, to: ref };
}

function getPreviousRange(
  type: FilterType,
  from: string,
  to: string,
): { from: string; to: string } {
  if (type === "year") {
    const y = parseInt(from.slice(0, 4), 10);
    return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
  }
  if (type === "day") {
    const d = new Date(from + "T00:00:00");
    d.setDate(d.getDate() - 1);
    return { from: localDate(d), to: localDate(d) };
  }
  if (type === "month") {
    const d = new Date(from + "T00:00:00");
    const prevFirst = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const prevLast = new Date(d.getFullYear(), d.getMonth(), 0);
    return {
      from: localDate(prevFirst),
      to: localDate(prevLast),
    };
  }
  // custom: same-length window shifted back
  const fromMs = new Date(from + "T00:00:00").getTime();
  const toMs = new Date(to + "T00:00:00").getTime();
  const durMs = toMs - fromMs + 86_400_000; // inclusive
  const prevTo = new Date(fromMs - 86_400_000);
  const prevFrom = new Date(prevTo.getTime() - durMs + 86_400_000);
  return {
    from: localDate(prevFrom),
    to: localDate(prevTo),
  };
}

function DateFilter({
  filterType,
  setFilterType,
  from,
  setFrom,
  to,
  setTo,
}: {
  filterType: FilterType;
  setFilterType: (t: FilterType) => void;
  from: string;
  setFrom: (v: string) => void;
  to: string;
  setTo: (v: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const todayMonth = today.slice(0, 7);
  const todayYear = today.slice(0, 4);

  const handleType = (t: FilterType) => {
    setFilterType(t);
    const range = getDefaultRange(t, today);
    setFrom(range.from);
    setTo(range.to);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(["month", "day", "year", "custom"] as FilterType[]).map((t) => (
        <button
          key={t}
          onClick={() => handleType(t)}
          className={clsx(
            "rounded-lg px-3 py-1.5 text-xs font-medium capitalize transition-colors",
            filterType === t
              ? "bg-[#2447d9] text-white"
              : "border border-[#e2e8f0] text-[#475569] hover:bg-[#f8fafc]",
          )}
        >
          {t}
        </button>
      ))}

      <div className="flex items-center gap-1.5 ml-1">
        <Calendar className="h-3.5 w-3.5 text-[#94a3b8]" />
        {filterType === "day" && (
          <input
            type="date"
            value={from}
            max={today}
            onChange={(e) => {
              setFrom(e.target.value);
              setTo(e.target.value);
            }}
            className="rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
        )}
        {filterType === "month" && (
          <input
            type="month"
            value={from.slice(0, 7)}
            max={todayMonth}
            onChange={(e) => {
              const r = getDefaultRange("month", `${e.target.value}-01`);
              setFrom(r.from);
              setTo(r.to);
            }}
            className="rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
        )}
        {filterType === "year" && (
          <input
            type="number"
            value={from.slice(0, 4)}
            min="2020"
            max={todayYear}
            onChange={(e) => {
              const y = e.target.value;
              setFrom(`${y}-01-01`);
              setTo(`${y}-12-31`);
            }}
            className="w-20 rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
          />
        )}
        {filterType === "custom" && (
          <>
            <input
              type="date"
              value={from}
              max={today}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
            <span className="text-[#94a3b8] text-xs">to</span>
            <input
              type="date"
              value={to}
              min={from}
              max={today}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-lg border border-[#e2e8f0] px-2 py-1 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
            />
          </>
        )}
      </div>
    </div>
  );
}

// ── % change badge ─────────────────────────────────────────────────────────────

function ChangeBadge({
  current,
  prev,
  showAbsDelta,
}: {
  current: number;
  prev: number | undefined;
  showAbsDelta?: boolean;
}) {
  if (prev === undefined || prev === 0) {
    return <span className="text-[#94a3b8] text-xs">—</span>;
  }
  const delta = current - prev;
  const pos = delta >= 0;
  if (showAbsDelta) {
    const better = pos; // for net position, higher = better
    return (
      <span
        className={clsx(
          "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium",
          better ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fee2e2] text-[#b91c1c]",
        )}
      >
        {better ? "▲" : "▼"} {formatCurrency(Math.abs(delta))}
      </span>
    );
  }
  const pct = (delta / Math.abs(prev)) * 100;
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium",
        pos ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fee2e2] text-[#b91c1c]",
      )}
    >
      {pos ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

// ── Large KPI tile ─────────────────────────────────────────────────────────────

function KpiTile({
  label,
  value,
  prevValue,
  tintBySign,
  subtitle,
  absDelta,
}: {
  label: string;
  value: number;
  prevValue: number | undefined;
  tintBySign?: boolean;
  subtitle?: string;
  absDelta?: boolean;
}) {
  const positive = value >= 0;
  return (
    <div
      className={clsx(
        "rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md",
        tintBySign
          ? positive
            ? "bg-[#f0fdf4] border-[#bbf7d0]"
            : "bg-[#fef2f2] border-[#fecaca]"
          : "bg-white border-[#e2e8f0]",
      )}
    >
      <p className="text-xs font-medium text-[#64748b] mb-1">{label}</p>
      <p
        className={clsx(
          "text-2xl font-bold leading-none",
          tintBySign
            ? positive
              ? "text-[#16a34a]"
              : "text-[#dc2626]"
            : "text-[#0f172a]",
        )}
      >
        {formatCurrency(value)}
      </p>
      {subtitle && (
        <p className="text-[10px] text-[#94a3b8] mt-1 mb-2">{subtitle}</p>
      )}
      <div className={clsx("flex items-center gap-2", subtitle ? "" : "mt-2")}>
        <ChangeBadge current={value} prev={prevValue} showAbsDelta={absDelta} />
        <span className="text-[10px] text-[#94a3b8]">vs prev period</span>
      </div>
    </div>
  );
}

// ── Compact KPI card (drill-down) ─────────────────────────────────────────────

function KpiCard({
  label,
  cat,
  accent,
  icon,
  onClick,
  subtitle,
}: {
  label: string;
  cat: FinancialCategoryTotal;
  accent: "green" | "red" | "orange" | "blue" | "slate";
  icon: React.ReactNode;
  onClick?: () => void;
  subtitle?: string;
}) {
  const accentMap = {
    green: {
      bg: "bg-[#f0fdf4]",
      text: "text-[#16a34a]",
      badge: "bg-[#dcfce7]",
      badgeText: "text-[#15803d]",
    },
    red: {
      bg: "bg-[#fef2f2]",
      text: "text-[#dc2626]",
      badge: "bg-[#fee2e2]",
      badgeText: "text-[#b91c1c]",
    },
    orange: {
      bg: "bg-[#fff7ed]",
      text: "text-[#ea580c]",
      badge: "bg-[#ffedd5]",
      badgeText: "text-[#c2410c]",
    },
    blue: {
      bg: "bg-[#eff6ff]",
      text: "text-[#2563eb]",
      badge: "bg-[#dbeafe]",
      badgeText: "text-[#1d4ed8]",
    },
    slate: {
      bg: "bg-[#f8fafc]",
      text: "text-[#475569]",
      badge: "bg-[#f1f5f9]",
      badgeText: "text-[#334155]",
    },
  };
  const a = accentMap[accent];

  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="text-left w-full rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm hover:shadow-md hover:border-[#2447d9]/30 transition-all disabled:cursor-default disabled:hover:shadow-sm disabled:hover:border-[#e2e8f0] group"
    >
      <div className="flex items-start justify-between mb-2">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-lg ${a.bg} ${a.text}`}
        >
          {icon}
        </div>
        {cat.count > 0 && (
          <span
            className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${a.badge} ${a.badgeText}`}
          >
            {cat.count}
          </span>
        )}
      </div>
      <p className="text-[11px] text-[#94a3b8] mb-0.5">{label}</p>
      <p className={`text-base font-bold ${a.text}`}>
        {formatCurrency(Math.abs(cat.total))}
      </p>
      {subtitle && (
        <p className="text-[10px] text-[#94a3b8] mt-0.5">{subtitle}</p>
      )}
      {onClick && cat.count > 0 && (
        <p className="text-[10px] text-[#2447d9] mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          View →
        </p>
      )}
    </button>
  );
}

// ── Donut center label helper ──────────────────────────────────────────────────

function renderDonutLabel(
  labelText: string,
  valueText: string,
): (props: unknown) => React.ReactElement | null {
  return (props) => {
    const p = props as { viewBox?: { cx?: number; cy?: number } };
    const cx = p.viewBox?.cx ?? 0;
    const cy = p.viewBox?.cy ?? 0;
    return (
      <text>
        <tspan
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          fontSize={11}
          fill="#94a3b8"
        >
          {labelText}
        </tspan>
        <tspan
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          fontSize={12}
          fontWeight={600}
          fill="#0f172a"
        >
          {valueText}
        </tspan>
      </text>
    );
  };
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function FinancialDashboardPage() {
  const { activeBusinessId, businesses, isSuperAdmin } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const defaultRange = getDefaultRange("month", today);

  const [filterType, setFilterType] = useState<FilterType>("month");
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [viewAll, setViewAll] = useState(false);
  const [drawerCategory, setDrawerCategory] = useState<string | null>(null);
  const [drawerLabel, setDrawerLabel] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);

  const activeBusinessName = businesses.find(
    (b) => b.businessId === activeBusinessId,
  )?.name;

  const prevRange = getPreviousRange(filterType, from, to);

  const { data, isLoading, error } = useQuery({
    queryKey: ["financials", viewAll ? "__all__" : activeBusinessId, from, to],
    queryFn: () =>
      viewAll
        ? financialsApi.getAllSummary(from, to)
        : financialsApi.getSummary(activeBusinessId!, from, to),
    enabled: viewAll || !!activeBusinessId,
    staleTime: 60_000,
  });

  const { data: prevData, isLoading: prevLoading } = useQuery({
    queryKey: ["financials", viewAll ? "__all__" : activeBusinessId, prevRange.from, prevRange.to],
    queryFn: () =>
      viewAll
        ? financialsApi.getAllSummary(prevRange.from, prevRange.to)
        : financialsApi.getSummary(activeBusinessId!, prevRange.from, prevRange.to),
    enabled: viewAll || !!activeBusinessId,
    staleTime: 60_000,
  });

  const loading = isLoading || prevLoading;

  const openDrawer = (category: string, label: string) => {
    if (viewAll) return; // drill-down not available in all-businesses view
    setDrawerCategory(category);
    setDrawerLabel(label);
  };

  // Guard: no business selected (skip if super-admin viewing all)
  if (!activeBusinessId && !viewAll) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#e2e8f0] bg-white py-20 text-center">
        <Building2 className="h-10 w-10 text-[#cbd5e1] mb-3" />
        <p className="text-sm font-medium text-[#64748b]">
          Select a business first
        </p>
        <p className="text-xs text-[#94a3b8] mt-1">
          Use the switcher in the top bar.
        </p>
      </div>
    );
  }

  // Derived chart data (safe to compute even without data)
  const costDonutData = data
    ? [
        {
          name: "Payroll",
          value: data.costs.payroll.total,
          fill: "#2447d9",
        },
        {
          name: "Store Expenses",
          value:
            data.costs.storeExpenses.total + data.costs.manualExpenses.total,
          fill: "#f97316",
        },
        {
          name: "Disbursements",
          value: data.costs.totalDisbursements,
          fill: "#8b5cf6",
        },
      ]
    : [];

  const revDonutData = data
    ? [
        {
          name: "Service Records",
          value: data.revenue.serviceRecords.total,
          fill: "#2447d9",
        },
        {
          name: "Store Income",
          value: data.revenue.storeIncome.total,
          fill: "#10b981",
        },
        {
          name: "Service Requests",
          value: data.revenue.serviceRequests.total,
          fill: "#f59e0b",
        },
        {
          name: "Manual Income",
          value: data.revenue.manualIncome.total,
          fill: "#8b5cf6",
        },
      ]
    : [];

  const recDonutData = data
    ? [
        {
          name: "Loan Repayments",
          value: data.recoveries.loanRepayments.total,
          fill: "#2447d9",
        },
        {
          name: "Cash Advance Repayments",
          value: data.recoveries.cashAdvanceRepayments.total,
          fill: "#10b981",
        },
        {
          name: "Wallet Advance Repayments",
          value: data.recoveries.walletAdvanceRepayments.total,
          fill: "#8b5cf6",
        },
      ]
    : [];

  const totalCosts = data?.costs.grandTotal ?? 0;

  return (
    <div className="space-y-5">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-[#0f172a]">
            Financial Dashboard
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5 text-[#2447d9]" />
            <span className="text-sm text-[#64748b]">
              <span className="font-medium text-[#2447d9]">
                {viewAll ? "All Businesses" : (activeBusinessName ?? activeBusinessId)}
              </span>
            </span>
            {isSuperAdmin && (
              <button
                onClick={() => setViewAll((v) => !v)}
                className={`ml-1 flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                  viewAll
                    ? "border-[#2447d9] bg-[#eff3ff] text-[#2447d9]"
                    : "border-[#e2e8f0] bg-white text-[#64748b] hover:border-[#2447d9] hover:text-[#2447d9]"
                }`}
                title={viewAll ? "Switch to single business view" : "View all businesses"}
              >
                {viewAll ? "All Businesses" : "View All"}
              </button>
            )}
          </div>
        </div>
        <DateFilter
          filterType={filterType}
          setFilterType={setFilterType}
          from={from}
          setFrom={setFrom}
          to={to}
          setTo={setTo}
        />
      </div>

      {/* ── Error ──────────────────────────────────────────────────── */}
      {error && (
        <div className="rounded-lg border border-[#fca5a5] bg-[#fef2f2] p-4 text-sm text-[#dc2626] flex items-center gap-2">
          {/* LoadingSpinner kept as fallback indicator */}
          {isLoading && <LoadingSpinner />}
          Failed to load financial data. Please try again.
        </div>
      )}

      {/* ── KPI strip (6 tiles, 3×2) ───────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <>
            <Skeleton className="h-[130px]" />
            <Skeleton className="h-[130px]" />
            <Skeleton className="h-[130px]" />
            <Skeleton className="h-[130px]" />
            <Skeleton className="h-[130px]" />
            <Skeleton className="h-[130px]" />
          </>
        ) : data ? (
          <>
            {/* Row 1: Revenue − Operating Costs = Gross Profit */}
            <KpiTile
              label="Gross Revenue"
              value={data.revenue.grossRevenue}
              prevValue={prevData?.revenue.grossRevenue}
              subtitle="Store records + income + service requests"
            />
            <KpiTile
              label="Operating Costs"
              value={data.costs.totalOperating}
              prevValue={prevData?.costs.totalOperating}
              subtitle="Payroll + store expenses + manual expenses"
            />
            <KpiTile
              label="Gross Profit"
              value={data.grossProfit}
              prevValue={prevData?.grossProfit}
              tintBySign
              subtitle="Revenue − operating costs"
            />
            {/* Row 2: Disbursements − Recoveries → Net Position */}
            <KpiTile
              label="Total Disbursements"
              value={data.costs.totalDisbursements}
              prevValue={prevData?.costs.totalDisbursements}
              subtitle="Loans + cash advances + encashments"
            />
            <KpiTile
              label="Recoveries"
              value={data.recoveries.total}
              prevValue={prevData?.recoveries.total}
              subtitle="Loan + advance repayments flowing back in"
            />
            <KpiTile
              label="Net Position"
              value={data.netPosition}
              prevValue={prevData?.netPosition}
              tintBySign
              absDelta
              subtitle="Gross profit − disbursements + recoveries"
            />
          </>
        ) : null}
      </div>

      {/* ── Middle row: combo chart (60%) + cost donut (40%) ──────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Combo chart */}
        <div className="lg:col-span-3 rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-[#0f172a] mb-4">
            Revenue vs Expenses Trend
          </h2>
          {loading ? (
            <Skeleton className="h-[260px]" />
          ) : data && data.trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart
                data={data.trend}
                margin={{ top: 4, right: 8, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#94a3b8" }}
                  tickFormatter={(v: number) =>
                    `₱${(v / 1000).toFixed(0)}k`
                  }
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    formatCurrency(value),
                    name,
                  ]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    borderColor: "#e2e8f0",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="revenue"
                  name="Revenue"
                  fill="#2447d9"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="expenses"
                  name="Expenses"
                  fill="#dc2626"
                  radius={[3, 3, 0, 0]}
                />
                <Line
                  dataKey="net"
                  name="Net"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={false}
                  type="monotone"
                />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[260px] items-center justify-center text-sm text-[#94a3b8]">
              No trend data available for this period
            </div>
          )}
          <p className="mt-3 text-[11px] text-[#94a3b8] leading-relaxed">
            <span className="font-medium text-[#2447d9]">Revenue</span> — store records, store income, service requests, manual income (net of reversals) ·{" "}
            <span className="font-medium text-[#dc2626]">Expenses</span> — payroll, store expenses, loans &amp; advance disbursements (net of reversals) ·{" "}
            <span className="font-medium text-[#10b981]">Net</span> — revenue minus expenses · Recoveries (repayments) are shown separately in the KPI cards below.
          </p>
        </div>

        {/* Cost breakdown donut */}
        <div className="lg:col-span-2 rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-[#0f172a]">Total Cost Breakdown</h2>
            <p className="text-[11px] text-[#94a3b8] mt-0.5">Operating costs + disbursements</p>
          </div>
          {loading ? (
            <Skeleton className="h-[260px]" />
          ) : data ? (
            <div>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={costDonutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={95}
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {costDonutData.map((entry, idx) => (
                      <Cell key={`cost-cell-${idx}`} fill={entry.fill} />
                    ))}
                    <Label
                      content={renderDonutLabel(
                        "Costs",
                        formatCurrency(totalCosts),
                      )}
                    />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="space-y-1.5 mt-3">
                {costDonutData.map((entry) => (
                  <div
                    key={entry.name}
                    className="flex items-center justify-between text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: entry.fill }}
                      />
                      <span className="text-[#475569]">{entry.name}</span>
                    </div>
                    <span className="font-medium text-[#0f172a]">
                      {formatCurrency(entry.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Revenue + Recoveries breakdown donuts ────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Revenue breakdown */}
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-[#0f172a] mb-4">Revenue Breakdown</h2>
          {loading ? (
            <Skeleton className="h-[200px]" />
          ) : data ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="flex-shrink-0">
                <PieChart width={200} height={200}>
                  <Pie
                    data={revDonutData}
                    cx={100} cy={100}
                    innerRadius={55} outerRadius={85}
                    dataKey="value" paddingAngle={2}
                  >
                    {revDonutData.map((entry, idx) => (
                      <Cell key={`rev-cell-${idx}`} fill={entry.fill} />
                    ))}
                    <Label content={renderDonutLabel("Revenue", formatCurrency(data.revenue.grossRevenue))} />
                  </Pie>
                </PieChart>
              </div>
              <div className="grid grid-cols-1 gap-2 min-w-[200px]">
                {revDonutData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.fill }} />
                      <span className="text-[#475569]">{entry.name}</span>
                    </div>
                    <span className="font-medium text-[#0f172a]">{formatCurrency(entry.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {/* Recoveries breakdown */}
        <div className="rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-[#0f172a] mb-4">Recoveries Breakdown</h2>
          {loading ? (
            <Skeleton className="h-[200px]" />
          ) : data ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="flex-shrink-0">
                <PieChart width={200} height={200}>
                  <Pie
                    data={recDonutData}
                    cx={100} cy={100}
                    innerRadius={55} outerRadius={85}
                    dataKey="value" paddingAngle={2}
                  >
                    {recDonutData.map((entry, idx) => (
                      <Cell key={`rec-cell-${idx}`} fill={entry.fill} />
                    ))}
                    <Label content={renderDonutLabel("Recoveries", formatCurrency(data.recoveries.total))} />
                  </Pie>
                </PieChart>
              </div>
              <div className="grid grid-cols-1 gap-2 min-w-[200px]">
                {recDonutData.map((entry) => (
                  <div key={entry.name} className="flex items-center justify-between gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.fill }} />
                      <span className="text-[#475569]">{entry.name}</span>
                    </div>
                    <span className="font-medium text-[#0f172a]">{formatCurrency(entry.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Transaction Breakdown (collapsible) ──────────────────────── */}
      <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
        <button
          onClick={() => setDetailsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-sm font-semibold text-[#0f172a] hover:bg-[#f8fafc] transition-colors"
        >
          <span>Transaction Breakdown</span>
          {detailsOpen ? (
            <ChevronUp className="h-4 w-4 text-[#64748b]" />
          ) : (
            <ChevronDown className="h-4 w-4 text-[#64748b]" />
          )}
        </button>

        {detailsOpen && (
          <div className="px-5 pb-5 space-y-5">
            {/* Revenue */}
            <div>
              <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-2">
                Revenue
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {loading ? (
                  <>
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                  </>
                ) : data ? (
                  <>
                    <KpiCard
                      label="Service Records"
                      cat={data.revenue.serviceRecords}
                      accent="green"
                      icon={<DollarSign className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("service-records", "Service Records")
                      }
                    />
                    <KpiCard
                      label="Store Income"
                      cat={data.revenue.storeIncome}
                      accent="green"
                      icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                      onClick={() => openDrawer("store-income", "Store Income")}
                    />
                    <KpiCard
                      label="Service Requests"
                      cat={data.revenue.serviceRequests}
                      accent="green"
                      icon={<Wallet className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("service-requests", "Service Requests")
                      }
                    />
                    <KpiCard
                      label="Manual Income"
                      cat={data.revenue.manualIncome}
                      accent="green"
                      icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("manual-income", "Manual Income")
                      }
                    />
                  </>
                ) : null}
              </div>
            </div>

            {/* Operating Costs */}
            <div>
              <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-2">
                Operating Costs
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {loading ? (
                  <>
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                  </>
                ) : data ? (
                  <>
                    <KpiCard
                      label="Payroll"
                      cat={data.costs.payroll}
                      accent="red"
                      icon={<CreditCard className="h-3.5 w-3.5" />}
                      onClick={() => openDrawer("payroll", "Payroll")}
                    />
                    <KpiCard
                      label="Store Expenses"
                      cat={data.costs.storeExpenses}
                      accent="red"
                      icon={<ArrowDownCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("store-expenses", "Store Expenses")
                      }
                    />
                    <KpiCard
                      label="Manual Expenses"
                      cat={data.costs.manualExpenses}
                      accent="red"
                      icon={<ArrowDownCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("manual-expenses", "Manual Expenses")
                      }
                    />
                  </>
                ) : null}
              </div>
            </div>

            {/* Disbursements */}
            <div>
              <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-2">
                Cash Disbursements{" "}
                <span className="ml-1 text-[#cbd5e1] normal-case font-normal">
                  money given out to employees
                </span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {loading ? (
                  <>
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                  </>
                ) : data ? (
                  <>
                    <KpiCard
                      label="Loan Disbursements"
                      cat={data.costs.loanDisbursements}
                      accent="orange"
                      icon={<ArrowDownCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("loans", "Loan Disbursements")
                      }
                    />
                    <KpiCard
                      label="Cash Advances"
                      cat={data.costs.cashAdvances}
                      accent="orange"
                      icon={<ArrowDownCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("cash-advances", "Cash Advances")
                      }
                    />
                    <KpiCard
                      label="Wallet Encashments"
                      cat={data.costs.walletEncashments}
                      accent="orange"
                      icon={<Wallet className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("wallet-encashments", "Wallet Encashments")
                      }
                    />
                    <KpiCard
                      label="Wallet Advances"
                      cat={data.costs.walletAdvances}
                      accent="orange"
                      icon={<Wallet className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("wallet-advances", "Wallet Advances")
                      }
                    />
                  </>
                ) : null}
              </div>
            </div>

            {/* Recoveries */}
            <div>
              <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wide mb-2">
                Recoveries{" "}
                <span className="ml-1 text-[#cbd5e1] normal-case font-normal">
                  repayments flowing back in
                </span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {loading ? (
                  <>
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                    <Skeleton className="h-[90px]" />
                  </>
                ) : data ? (
                  <>
                    <KpiCard
                      label="Loan Repayments"
                      cat={data.recoveries.loanRepayments}
                      accent="blue"
                      icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer("loan-repayments", "Loan Repayments")
                      }
                    />
                    <KpiCard
                      label="Cash Advance Repayments"
                      cat={data.recoveries.cashAdvanceRepayments}
                      accent="blue"
                      icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer(
                          "cash-advance-repayments",
                          "Cash Advance Repayments",
                        )
                      }
                    />
                    <KpiCard
                      label="Wallet Advance Repayments"
                      cat={data.recoveries.walletAdvanceRepayments}
                      accent="blue"
                      icon={<ArrowUpCircle className="h-3.5 w-3.5" />}
                      onClick={() =>
                        openDrawer(
                          "wallet-advance-repayments",
                          "Wallet Advance Repayments",
                        )
                      }
                    />
                  </>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Transactions drawer (single-business only) ──────────────── */}
      {drawerCategory && activeBusinessId && !viewAll && (
        <TransactionsDrawer
          isOpen={drawerCategory !== null}
          onClose={() => setDrawerCategory(null)}
          businessId={activeBusinessId}
          category={drawerCategory}
          label={drawerLabel}
          from={from}
          to={to}
        />
      )}
    </div>
  );
}
